import { useParams, useNavigate } from "react-router-dom";
import { JOB_ACTIVE } from "../constants";
import { formatTime } from "../utils/formatTime";
import AddToCalendar from "./AddToCalendar";
import Breadcrumb from "./Breadcrumb";
import styles from "./JobPage.module.css";

export default function JobPage({ jobs, currentUser, loading, onSignup, onWithdraw, onJoinWaitlist, onLeaveWaitlist }) {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const job = jobs.find((j) => j.id === jobId);

  if (loading) {
    return <p className={styles.loading}>Loading...</p>;
  }

  if (!job) {
    return (
      <div className={styles.notFound}>
        <h2>Job Not Found</h2>
        <p>This assignment may have been removed or is no longer available.</p>
        <button className={styles.backBtn} onClick={() => navigate("/")}>Back to Dashboard</button>
      </div>
    );
  }

  const signups = job.signups || [];
  const isFull = signups.length >= job.slotsTotal;
  const isSignedUp = signups.includes(currentUser.id);
  const isWaitlisted = (job.waitlist || []).includes(currentUser.id);
  const isBlocked = (job.blockedOfficers || []).includes(currentUser.id);
  const isActive = job.status === JOB_ACTIVE;

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const [y, m, d] = dateStr.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString("en-US", {
      weekday: "long", year: "numeric", month: "long", day: "numeric",
    });
  };

  return (
    <div className={styles.container}>
      <Breadcrumb items={[
        { label: "Dashboard", path: "/" },
        { label: job.title },
      ]} />

      <div className={styles.card}>
        <div className={styles.header}>
          <h2 className={styles.title}>{job.title}</h2>
          <span className={`${styles.badge} ${
            !isActive ? styles.badgeClosed :
            isFull ? styles.badgeFull : styles.badgeOpen
          }`}>
            {!isActive ? job.status : isFull ? "Full" : "Open"}
          </span>
        </div>

        <div className={styles.details}>
          <div className={styles.row}>
            <span className={styles.label}>Date</span>
            <span className={styles.value}>{formatDate(job.date)}</span>
          </div>
          <div className={styles.row}>
            <span className={styles.label}>Time</span>
            <span className={styles.value}>{formatTime(job.startTime)} &ndash; {formatTime(job.endTime)}</span>
          </div>
          {job.location && (
            <div className={styles.row}>
              <span className={styles.label}>Location</span>
              <span className={styles.value}>{job.location}</span>
            </div>
          )}
          <div className={styles.row}>
            <span className={styles.label}>Pay Rate</span>
            <span className={styles.value}>${job.payRate}/hr</span>
          </div>
          <div className={styles.row}>
            <span className={styles.label}>Slots</span>
            <span className={styles.value}>{signups.length}/{job.slotsTotal} officers</span>
          </div>
          {job.entity && (
            <div className={styles.row}>
              <span className={styles.label}>Entity</span>
              <span className={styles.value}>{job.entity}</span>
            </div>
          )}
          {job.pointOfContact && (
            typeof job.pointOfContact === "string" ? (
              <div className={styles.row}>
                <span className={styles.label}>Contact</span>
                <span className={styles.value}>{job.pointOfContact}</span>
              </div>
            ) : Array.isArray(job.pointOfContact) && job.pointOfContact.filter((c) => c.name || c.phone || c.email).length > 0 ? (
              job.pointOfContact.filter((c) => c.name || c.phone || c.email).map((c, i, arr) => (
                <div key={i} className={styles.row}>
                  <span className={styles.label}>Contact{arr.length > 1 ? ` ${i + 1}` : ""}</span>
                  <span className={styles.value}>
                    {c.name}
                    {c.phone && <>{c.name ? " \u00B7 " : ""}<a href={`tel:${c.phone}`}>{c.phone}</a></>}
                    {c.email && <>{(c.name || c.phone) ? " \u00B7 " : ""}<a href={`mailto:${c.email}`}>{c.email}</a></>}
                  </span>
                </div>
              ))
            ) : null
          )}
        </div>

        {job.details && (
          <div className={styles.description}>
            <span className={styles.label}>Details</span>
            <p className={styles.descText}>{job.details}</p>
          </div>
        )}

        {(job.attachments || []).length > 0 && (
          <div className={styles.description}>
            <span className={styles.label}>Attachments</span>
            <div className={styles.attachList}>
              {job.attachments.map((att, i) => (
                <a key={i} href={att.url} target="_blank" rel="noopener noreferrer" className={styles.attachItem}>
                  {att.name}
                </a>
              ))}
            </div>
          </div>
        )}

        {isActive && (
          <div className={styles.calendarExport}>
            <AddToCalendar jobs={job} />
          </div>
        )}

        {isActive && !isBlocked && (
          <div className={styles.actions}>
            {isSignedUp ? (
              <button className={styles.withdrawBtn} onClick={() => onWithdraw(job.id)}>Withdraw</button>
            ) : isWaitlisted ? (
              <button className={styles.leaveWaitlistBtn} onClick={() => onLeaveWaitlist(job.id)}>Leave Waitlist</button>
            ) : isFull ? (
              <button className={styles.waitlistBtn} onClick={() => onJoinWaitlist(job.id)}>Join Waitlist</button>
            ) : (
              <button className={styles.signupBtn} onClick={() => onSignup(job.id)}>Sign Up</button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
